import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaSignOutAlt, FaCheckCircle, FaUpload, FaCalendarAlt, FaArrowRight, FaEye, FaDownload } from 'react-icons/fa';
import { accommodationAPI } from '../../api';
import './AccommodationBooking.css';

const AccommodationBooking = () => {
  const [user, setUser] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    checkInDate: '',
    checkOutDate: '',
    transactionDetails: ''
  });
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [accommodationStatus, setAccommodationStatus] = useState(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [numberOfDays, setNumberOfDays] = useState(0);
  const [bookingDetails, setBookingDetails] = useState(null); // Store booking details for confirmation page
  const navigate = useNavigate();

  const ACCOMMODATION_RATE_PER_DAY = 250;
  const ACCOMMODATION_DEADLINE = new Date('2026-07-15'); // Assumed accommodation deadline

  const BANK_DETAILS = {
    accountName: 'Indian Institute of Information Technology Design and Manufacturing Kurnool-Hostel',
    accountNumber: '50100517285898',
    ifscCode: 'HDFC0004159',
    bankName: 'HDFC Bank',
    branch: 'C. Camp Center, Kurnool',
    micrCode: '518229009'
  };

  useEffect(() => {
    const loggedInUser = localStorage.getItem('user');
    if (loggedInUser) {
      const user = JSON.parse(loggedInUser);
      setUser(user);
      
      const fetchAccommodation = async () => {
        const result = await accommodationAPI.getAccommodation(user.username);
        if (result.success && result.accommodation) {
          setAccommodationStatus(result.accommodation);
          setStep(4); // Show success/booked state
        }
      };
      
      fetchAccommodation();
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Calculate number of days between check-in and check-out (including time)
  // If stay is more than 24 hours (even 1 hour over), it counts as additional day
  const calculateNumberOfDays = () => {
    if (formData.checkInDate && formData.checkOutDate) {
      const checkIn = new Date(formData.checkInDate);
      const checkOut = new Date(formData.checkOutDate);
      const diffTime = checkOut - checkIn;
      const hours = diffTime / (1000 * 60 * 60);
      const days = Math.ceil(hours / 24);
      return days > 0 ? days : 0;
    }
    return 0;
  };

  useEffect(() => {
    setNumberOfDays(calculateNumberOfDays());
  }, [formData.checkInDate, formData.checkOutDate]);

  const calculateAccommodationCost = () => {
    return numberOfDays * ACCOMMODATION_RATE_PER_DAY;
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleTransactionDetailsChange = (e) => {
    setFormData({
      ...formData,
      transactionDetails: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      
      if (file.size > maxSize) {
        setError('File size must be less than 5MB');
        return;
      }
      
      if (!allowedTypes.includes(file.type)) {
        setError('Only PDF, JPEG, and PNG files are allowed');
        return;
      }
      
      setPaymentProofFile(file);
      setError('');
    }
  };

  const handleProceedToPayment = () => {
    if (!formData.checkInDate || !formData.checkOutDate) {
      setError('Please select both check-in and check-out dates');
      return;
    }
    
    const checkInDate = new Date(formData.checkInDate);
    const checkOutDate = new Date(formData.checkOutDate);
    
    if (checkOutDate <= checkInDate) {
      setError('Check-out date must be after check-in date');
      return;
    }
    
    if (numberOfDays <= 0) {
      setError('Check-out date must be after check-in date');
      return;
    }
    
    setError('');
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentProofFile) {
      setError('Please upload payment proof file');
      return;
    }
    
    if (!formData.transactionDetails.trim()) {
      setError('Please enter transaction details');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    const result = await accommodationAPI.submit(
      user.username,
      user.id,
      {
        checkInDate: formData.checkInDate,
        checkOutDate: formData.checkOutDate,
        transactionDetails: formData.transactionDetails
      },
      paymentProofFile
    );
    
    setIsSubmitting(false);
    
    if (result.success) {
      // Store booking details for confirmation page display BEFORE clearing formData
      setBookingDetails({
        checkInDate: formData.checkInDate,
        checkOutDate: formData.checkOutDate,
        numberOfDays: numberOfDays,
        totalAmount: calculateAccommodationCost()
      });
      
      setStep(3);
      setFormData({
        checkInDate: '',
        checkOutDate: '',
        transactionDetails: ''
      });
      setPaymentProofFile(null);
    } else {
      setError(result.message || 'Failed to submit accommodation booking');
    }
  };

  if (!user) {
    return <div className="accommodation-container">Loading...</div>;
  }

  return (
    <div className="accommodation-page">
      <div className="accommodation-header">
        <div className="accommodation-user-section">
          <FaUser className="accommodation-user-icon" />
          <span className="accommodation-username">{user.username}</span>
          <div className="accommodation-profile-dropdown-container">
            <button
              className="accommodation-dropdown-btn"
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
            >
              ▼
            </button>
            {showProfileDropdown && (
              <div className="accommodation-dropdown-menu">
                <button className="accommodation-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="accommodation-container">
        <h1 className="accommodation-title">Book Your Accommodation</h1>
        <p className="accommodation-subtitle">Select your stay dates and upload payment proof</p>

        {/* Progress Steps */}
        <div className="accommodation-steps">
          <div className={`accommodation-step ${step >= 1 ? 'active' : ''}`}>
            <div className="accommodation-step-number">1</div>
            <p className="accommodation-step-label">Select Dates</p>
          </div>
          <div className={`accommodation-step ${step >= 2 ? 'active' : ''}`}>
            <div className="accommodation-step-number">2</div>
            <p className="accommodation-step-label">Payment Details</p>
          </div>
          <div className={`accommodation-step ${step >= 3 ? 'active' : ''}`}>
            <div className="accommodation-step-number">3</div>
            <p className="accommodation-step-label">Confirmation</p>
          </div>
        </div>

        {error && <div className="accommodation-error">{error}</div>}

        {/* STEP 1: Select Dates */}
        {step === 1 && (
          <div className="accommodation-step-content">
            <div className="accommodation-form-section">
              <h2>Select Your Stay Dates</h2>
              
              <div className="accommodation-dates-grid">
                <div className="accommodation-form-group">
                  <label className="accommodation-label">
                    <FaCalendarAlt className="accommodation-icon" />
                    Check-in Date & Time (24 Hour Format)
                  </label>
                  <input
                    type="datetime-local"
                    name="checkInDate"
                    value={formData.checkInDate}
                    onChange={handleDateChange}
                    className="accommodation-input"
                    required
                  />
                </div>

                <div className="accommodation-form-group">
                  <label className="accommodation-label">
                    <FaCalendarAlt className="accommodation-icon" />
                    Check-out Date & Time (24 Hour Format)
                  </label>
                  <input
                    type="datetime-local"
                    name="checkOutDate"
                    value={formData.checkOutDate}
                    onChange={handleDateChange}
                    className="accommodation-input"
                    min={formData.checkInDate ? formData.checkInDate : ''}
                    required
                  />
                  {formData.checkInDate && formData.checkOutDate && (
                    <small className={`accommodation-date-validation ${new Date(formData.checkOutDate) > new Date(formData.checkInDate) ? 'valid' : 'invalid'}`}>
                      {new Date(formData.checkOutDate) > new Date(formData.checkInDate) 
                        ? '✓ Valid date & time range' 
                        : '✗ Check-out must be after check-in'}
                    </small>
                  )}
                </div>
              </div>
              
              <div className="accommodation-time-info">
                <p><strong>⏱️ Note:</strong> Duration is calculated in 24-hour cycles. Any stay exceeding 24 hours counts as an additional day. For example: 25 hours = 2 days charge, 48.5 hours = 3 days charge.</p>
              </div>

              {formData.checkInDate && formData.checkOutDate && numberOfDays > 0 && (
                <div className="accommodation-cost-summary">
                  <div className="accommodation-cost-item">
                    <span>Number of Days:</span>
                    <strong>{numberOfDays} days</strong>
                  </div>
                  <div className="accommodation-cost-item">
                    <span>Rate per Day:</span>
                    <strong>₹{ACCOMMODATION_RATE_PER_DAY}</strong>
                  </div>
                  <div className="accommodation-cost-item accommodation-total-cost">
                    <span>Total Amount:</span>
                    <strong>₹{calculateAccommodationCost()}</strong>
                  </div>
                </div>
              )}

              <button
                className="accommodation-btn accommodation-btn-primary"
                onClick={handleProceedToPayment}
                disabled={!formData.checkInDate || !formData.checkOutDate || (formData.checkInDate && formData.checkOutDate && new Date(formData.checkOutDate) <= new Date(formData.checkInDate))}
              >
                Continue to Payment <FaArrowRight />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Payment Details */}
        {step === 2 && (
          <div className="accommodation-step-content">
            <div className="accommodation-payment-section">
              <h2>Payment Details & Upload Proof</h2>

              {/* Booking Summary */}
              <div className="accommodation-booking-summary">
                <h3>Booking Summary</h3>
                <div className="accommodation-summary-grid">
                  <div className="accommodation-summary-item">
                    <label>Check-in:</label>
                  <p>{new Date(formData.checkInDate).toLocaleString()}</p>
                </div>
                <div className="accommodation-summary-item">
                  <label>Check-out:</label>
                  <p>{new Date(formData.checkOutDate).toLocaleString()}</p>
                  </div>
                  <div className="accommodation-summary-item">
                    <label>Number of Days:</label>
                    <p>{numberOfDays} days @ ₹{ACCOMMODATION_RATE_PER_DAY}/day</p>
                  </div>
                  <div className="accommodation-summary-item accommodation-summary-total">
                    <label>Total Amount:</label>
                    <p className="accommodation-amount">₹{calculateAccommodationCost()}</p>
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div className="accommodation-bank-details-section">
                <h3>Transfer the Amount to This Account</h3>
                <div className="accommodation-bank-details-card">
                  <div className="accommodation-detail-row">
                    <span className="accommodation-detail-label">Bank Name:</span>
                    <span className="accommodation-detail-value">{BANK_DETAILS.bankName}</span>
                  </div>
                  <div className="accommodation-detail-row">
                    <span className="accommodation-detail-label">Account Name:</span>
                    <span className="accommodation-detail-value">{BANK_DETAILS.accountName}</span>
                  </div>
                  <div className="accommodation-detail-row">
                    <span className="accommodation-detail-label">Account Number:</span>
                    <span className="accommodation-detail-value">{BANK_DETAILS.accountNumber}</span>
                  </div>
                  <div className="accommodation-detail-row">
                    <span className="accommodation-detail-label">IFSC Code:</span>
                    <span className="accommodation-detail-value">{BANK_DETAILS.ifscCode}</span>
                  </div>
                  <div className="accommodation-detail-row">
                    <span className="accommodation-detail-label">Branch:</span>
                    <span className="accommodation-detail-value">{BANK_DETAILS.branch}</span>
                  </div>
                  <div className="accommodation-detail-row">
                    <span className="accommodation-detail-label">MICR Code:</span>
                    <span className="accommodation-detail-value">{BANK_DETAILS.micrCode}</span>
                  </div>
                </div>

                <div className="accommodation-qr-section">
                  <h4>Hostel Payment QR Code</h4>
                  <div className="accommodation-qr-placeholder">
                    <img 
                      src="/hostel.png" 
                      alt="Hostel Payment QR Code" 
                      className="accommodation-qr-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '[QR Code for Hostel Payment]<br/><small>Scan to transfer ₹' + calculateAccommodationCost() + '</small>';
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Form Section */}
              <form onSubmit={handleSubmit} className="accommodation-form">
                <div className="accommodation-form-group">
                  <label className="accommodation-label">Transaction Details / Reference Number</label>
                  <input
                    type="text"
                    name="transactionDetails"
                    value={formData.transactionDetails}
                    onChange={handleTransactionDetailsChange}
                    placeholder="e.g., UTR number, transaction ID, or bank transfer reference"
                    className="accommodation-input"
                    required
                  />
                </div>

                <div className="accommodation-form-group">
                  <label className="accommodation-label">
                    <FaUpload className="accommodation-icon" />
                    Upload Payment Proof
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="accommodation-file-input"
                    required
                  />
                  <small className="accommodation-file-hint">PDF, JPEG, or PNG (Max 5MB)</small>
                  {paymentProofFile && (
                    <div className="accommodation-file-selected">
                      ✓ {paymentProofFile.name}
                    </div>
                  )}
                </div>

                <div className="accommodation-button-group">
                  <button
                    type="button"
                    className="accommodation-btn accommodation-btn-secondary"
                    onClick={() => setStep(1)}
                  >
                    ← Back
                  </button>
                  <button
                    type="submit"
                    className="accommodation-btn accommodation-btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Booking'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* STEP 3: Confirmation */}
        {step === 3 && bookingDetails && (
          <div className="accommodation-step-content">
            <div className="accommodation-success-section">
              <FaCheckCircle className="accommodation-success-icon" />
              <h2>Booking Submitted Successfully!</h2>
              <p className="accommodation-success-message">
                Your accommodation booking has been submitted. You'll receive a confirmation email shortly.
              </p>

              <div className="accommodation-confirmation-details">
                <h3>Booking Details</h3>
                <div className="accommodation-detail-grid">
                  <div className="accommodation-detail-item">
                    <label>Check-in:</label>
                    <p>{new Date(bookingDetails.checkInDate).toLocaleString()}</p>
                  </div>
                  <div className="accommodation-detail-item">
                    <label>Check-out:</label>
                    <p>{new Date(bookingDetails.checkOutDate).toLocaleString()}</p>
                  </div>
                  <div className="accommodation-detail-item">
                    <label>Number of Days:</label>
                    <p>{bookingDetails.numberOfDays} days</p>
                  </div>
                  <div className="accommodation-detail-item">
                    <label>Total Amount:</label>
                    <p>₹{bookingDetails.totalAmount}</p>
                  </div>
                </div>
              </div>

              <div className="accommodation-button-group">
                <button
                  className="accommodation-btn accommodation-btn-primary"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: Already Booked */}
        {step === 4 && accommodationStatus && (
          <div className="accommodation-step-content">
            <div className="accommodation-booked-section">
              <FaCheckCircle className="accommodation-success-icon" />
              <h2>You Have Already Booked Accommodation</h2>
              
              <div className="accommodation-booked-details">
                <h3>Your Booking Details</h3>
                <div className="accommodation-detail-grid">
                  <div className="accommodation-detail-item">
                    <label>Check-in Date:</label>
                    <p>{new Date(accommodationStatus.checkInDate).toLocaleDateString()}</p>
                  </div>
                  <div className="accommodation-detail-item">
                    <label>Check-out Date:</label>
                    <p>{new Date(accommodationStatus.checkOutDate).toLocaleDateString()}</p>
                  </div>
                  <div className="accommodation-detail-item">
                    <label>Number of Days:</label>
                    <p>{accommodationStatus.numberOfDays} days</p>
                  </div>
                  <div className="accommodation-detail-item">
                    <label>Total Amount:</label>
                    <p>₹{accommodationStatus.accommodationAmount}</p>
                  </div>
                  <div className="accommodation-detail-item">
                    <label>Status:</label>
                    <p><span className={`accommodation-status-badge accommodation-status-${accommodationStatus.accommodationStatus}`}>{accommodationStatus.accommodationStatus?.toUpperCase()}</span></p>
                  </div>
                  <div className="accommodation-detail-item">
                    <label>Payment Date:</label>
                    <p>{accommodationStatus.paymentDate ? new Date(accommodationStatus.paymentDate).toLocaleDateString() : '-'}</p>
                  </div>
                </div>
              </div>

              <div className="accommodation-button-group">
                <button
                  className="accommodation-btn accommodation-btn-primary"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccommodationBooking;
